import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Alert,
  Loader,
  Center,
  Paper,
  Table,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDownload,
  IconFileText,
  IconCalendar,
  IconPackage,
  IconClipboardList,
  IconUserPlus,
  IconUserMinus,
  IconCheck,
  IconClock,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { publicTenderApi, PublicTender } from '../../../api/publicTenders';
import { useVendorAuth } from '../auth/VendorAuthContext';
import { useTenderRegistrationStatus, useRegisterForTender, useWithdrawFromTender } from '../hooks/useTenderRegistrations';
import { notifications } from '@mantine/notifications';
import { useBidSubmissions } from '../../tenders/hooks/useBidSubmissions';
import { bidSubmissionApi, CreateBidSubmissionDto } from '../../../api/bidSubmissions';
import { useVendorProfile } from '../hooks/useVendors';

// Extend PublicTender to include items and requirements
interface TenderDetailData extends PublicTender {
  requireRegistration?: boolean;
  requirePrebidMeeting?: boolean;
  prebidMeetingDate?: string;
  prebidMeetingVenue?: string;
  items?: {
    id: string;
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    order: number;
  }[];
  requirements?: {
    id: string;
    documentType: string;
    description?: string;
    isRequired: boolean;
    order: number;
  }[];
}

export function VendorTenderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vendor } = useVendorAuth();
  const queryClient = useQueryClient();
  const [downloadingTor, setDownloadingTor] = useState(false);
  const { data: vendorProfile } = useVendorProfile();

  const { data: tender, isLoading, error } = useQuery<TenderDetailData, Error>({
    queryKey: ['public-tender-detail', id],
    queryFn: async () => {
      const result = await publicTenderApi.getTenderById(id!);
      return result as TenderDetailData;
    },
    enabled: !!id,
  });

  const { data: registrationStatus, isLoading: loadingRegistrationStatus, refetch: refetchRegistrationStatus } = useTenderRegistrationStatus(id!);
  const registerForTender = useRegisterForTender();
  const withdrawFromTender = useWithdrawFromTender();
  const { data: bidSubmissions, refetch: refetchBidSubmissions } = useBidSubmissions(id!);
  
  // Check for draft bid submission
  const { data: draftSubmission } = useQuery({
    queryKey: ['bid-submission-draft', id],
    queryFn: () => bidSubmissionApi.getDraftBidSubmission(id!, true), // Use vendor auth
    enabled: !!id && !!vendor,
  });

  // Check for any existing submission (draft or submitted) for this vendor
  const { data: vendorSubmissions } = useQuery({
    queryKey: ['vendor-bid-submissions'],
    queryFn: () => bidSubmissionApi.getVendorBidSubmissions(),
    enabled: !!vendor,
  });

  const existingSubmissionForTender = vendorSubmissions?.find(
    sub => sub.tenderId === id && sub.deletedAt === null
  );

  const isRegistered = registrationStatus?.registered || false;
  const deadlinePassed = tender ? dayjs().isAfter(dayjs(tender.submissionDeadline)) : false;
  const canSubmitBid = tender && !deadlinePassed && (tender.stage === 'PUBLISHED' || tender.stage === 'COLLECTION');
  const vendorBidSubmission = bidSubmissions?.find(bid => bid.vendorId === vendor?.id && bid.status === 'SUBMITTED');
  const hasExistingSubmission = !!existingSubmissionForTender;
  const hasSubmittedBid = existingSubmissionForTender?.status === 'SUBMITTED';

  const handleDownloadTor = async () => {
    if (!tender?.id) return;
    setDownloadingTor(true);
    try {
      await publicTenderApi.downloadTorDocument(tender.id);
    } catch (err) {
      notifications.show({
        title: 'Download Failed',
        message: 'Failed to download TOR document. Please try again.',
        color: 'red',
      });
    } finally {
      setDownloadingTor(false);
    }
  };

  const handleRegister = async () => {
    if (!vendor) {
      notifications.show({
        title: 'Authentication Required',
        message: 'Please log in as a vendor to register for tenders.',
        color: 'blue',
      });
      return;
    }
    if (!tender?.id) return;
    try {
      await registerForTender.mutateAsync(tender.id);
      refetchRegistrationStatus();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleWithdraw = async () => {
    if (!tender?.id) return;
    try {
      await withdrawFromTender.mutateAsync(tender.id);
      refetchRegistrationStatus();
    } catch (err) {
      // Error handled by hook
    }
  };

  // Create draft bid submission with one click
  const createDraftMutation = useMutation({
    mutationFn: async (tenderId: string) => {
      // Check if vendor already has a submission for this tender
      const existingSub = vendorSubmissions?.find(
        sub => sub.tenderId === tenderId && sub.deletedAt === null
      );

      if (existingSub) {
        if (existingSub.status === 'SUBMITTED') {
          throw new Error('You have already submitted a bid for this tender. Only one submission per tender is allowed.');
        } else if (existingSub.status === 'SAVED') {
          // If there's a draft, navigate to it instead of creating a new one
          navigate(`/vendor/submissions/${tenderId}/${existingSub.id}`);
          return existingSub;
        }
      }

      const draftData: CreateBidSubmissionDto = {
        companyName: vendorProfile?.businessName || '',
        totalPrice: 0,
        representativeName: '',
        representativeTitle: '',
        representativePhone: vendorProfile?.phone || '',
        representativeEmail: vendorProfile?.email || '',
        companyAddress: vendorProfile?.address || '',
        registrationNumber: vendorProfile?.registrationNumber || '',
        taxId: vendorProfile?.taxId || '',
        status: 'SAVED' as const,
        bidItems: [],
      };
      return await bidSubmissionApi.createBidSubmission(tenderId, draftData, true); // Use vendor auth
    },
    onSuccess: (data, tenderId) => {
      queryClient.invalidateQueries({ queryKey: ['bid-submission-draft', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-bid-submissions'] });
      if (data && data.id) {
        navigate(`/vendor/submissions/${tenderId}/${data.id}`);
      } else {
        navigate(`/vendor/tenders/${tenderId}/submit-bid`);
      }
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to create draft bid. Please try again.',
        color: 'red',
      });
    },
  });

  const handleCreateDraftAndNavigate = async () => {
    if (!tender?.id) return;
    if (tender?.requireRegistration && !isRegistered) {
      notifications.show({
        title: 'Registration Required',
        message: 'You must register for this tender before submitting a bid.',
        color: 'red',
      });
      return;
    }
    
    // Check if vendor already has a submitted bid
    if (hasSubmittedBid) {
      notifications.show({
        title: 'Already Submitted',
        message: 'You have already submitted a bid for this tender. Only one submission per tender is allowed.',
        color: 'yellow',
      });
      if (existingSubmissionForTender?.tender?.id) {
        navigate(`/vendor/submissions/${existingSubmissionForTender.tender.id}/${existingSubmissionForTender.id}`);
      }
      return;
    }

    await createDraftMutation.mutateAsync(tender.id);
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error || !tender) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconClock size={16} />} color="red">
          Failed to load tender details: {error?.message || 'Tender not found or not published.'}
        </Alert>
      </Container>
    );
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'GOODS': return 'blue';
      case 'SERVICES': return 'green';
      case 'WORKS': return 'orange';
      default: return 'gray';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'PUBLISHED': return 'green';
      case 'COLLECTION': return 'blue';
      case 'OPENED': return 'yellow';
      case 'TECH_EVAL': return 'purple';
      case 'FIN_EVAL': return 'indigo';
      case 'AWARDED': return 'teal';
      default: return 'gray';
    }
  };

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            color="green"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/vendor/tenders')}
          >
            Back to Browse Tenders
          </Button>
        </Group>

        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2}>{tender.description}</Title>
            <Text c="dimmed" size="sm">
              {tender.referenceNo} • Published on{' '}
              {dayjs(tender.publishingDate).format('MMM DD, YYYY')}
            </Text>
          </div>
          <Group>
            <Badge color={getCategoryColor(tender.category)} size="sm">
              {tender.category}
            </Badge>
            <Badge color={getStageColor(tender.stage)} size="sm">
              {tender.stage}
            </Badge>
            {tender.requireRegistration && (
              <Badge color="orange" size="sm">
                Registration Required
              </Badge>
            )}
            {isRegistered && (
              <Badge color="green" size="sm" leftSection={<IconCheck size={12} />}>
                Registered
              </Badge>
            )}
          </Group>
        </Group>

        <Stack gap="xl">
          {tender?.requirePrebidMeeting && tender?.prebidMeetingDate && (
            <Card shadow="sm" p="md" withBorder>
              <Group>
                <IconCalendar size={20} />
                <Stack gap={0}>
                  <Text fw={500}>Pre-Bid Meeting</Text>
                  <Text size="sm" c="dimmed">
                    Date: {dayjs(tender.prebidMeetingDate).format('MMM DD, YYYY HH:mm')}
                  </Text>
                  {tender.prebidMeetingVenue && (
                    <Text size="sm" c="dimmed">
                      Venue: {tender.prebidMeetingVenue}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Card>
          )}

          <Card shadow="sm" p="md" withBorder>
            <Title order={4} mb="md">
              Key Dates
            </Title>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Publishing Date
                </Text>
                <Text>{dayjs(tender.publishingDate).format('MMM DD, YYYY HH:mm')}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Submission Deadline
                </Text>
                <Text color={deadlinePassed ? 'red' : 'inherit'}>
                  {dayjs(tender.submissionDeadline).format('MMM DD, YYYY HH:mm')}
                </Text>
              </Group>
              {tender.openingDate && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Opening Date
                  </Text>
                  <Text>{dayjs(tender.openingDate).format('MMM DD, YYYY HH:mm')}</Text>
                </Group>
              )}
            </Stack>
          </Card>

          {tender.torSpecification && (
            <Card shadow="sm" p="md" withBorder>
              <Title order={4} mb="md">
                Terms of Reference (TOR)
              </Title>
              <div
                dangerouslySetInnerHTML={{ __html: tender.torSpecification }}
                style={{
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                }}
              />
              {tender.torDocument && (
                <Button
                  mt="md"
                  variant="light"
                  color="green"
                  leftSection={<IconDownload size={16} />}
                  onClick={handleDownloadTor}
                  loading={downloadingTor}
                >
                  Download TOR Document ({tender.torDocument.fileName})
                </Button>
              )}
            </Card>
          )}

          {tender?.items && tender.items.length > 0 && (
            <Card shadow="sm" p="md" withBorder>
              <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconPackage size={20} />
                Tender Items
              </Title>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Item Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Quantity</Table.Th>
                    <Table.Th>Unit</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tender.items.map((item: any) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>{item.order}</Table.Td>
                      <Table.Td>{item.itemName}</Table.Td>
                      <Table.Td>{item.description || '-'}</Table.Td>
                      <Table.Td>{item.quantity}</Table.Td>
                      <Table.Td>{item.unit}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}

          {tender?.requirements && tender.requirements.length > 0 && (
            <Card shadow="sm" p="md" withBorder>
              <Title order={4} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconClipboardList size={20} />
                Required Documents
              </Title>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Document Type</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Required</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tender.requirements.map((req: any) => (
                    <Table.Tr key={req.id}>
                      <Table.Td>{req.order}</Table.Td>
                      <Table.Td>{req.documentType}</Table.Td>
                      <Table.Td>{req.description || '-'}</Table.Td>
                      <Table.Td>{req.isRequired ? 'Yes' : 'No'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}

          {/* Bid Submission Status */}
          {((hasSubmittedBid && existingSubmissionForTender) || vendorBidSubmission) && (
            <Card shadow="sm" p="md" withBorder>
              <Title order={4} mb="md">
                Your Bid Submission
              </Title>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Status</Text>
                  <Badge color="green">Submitted</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Total Price</Text>
                  <Text fw={500}>${Number(existingSubmissionForTender?.totalPrice || vendorBidSubmission?.totalPrice || 0).toFixed(2)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Submitted At</Text>
                  <Text>{(existingSubmissionForTender?.submittedAt || vendorBidSubmission?.submittedAt) ? dayjs(existingSubmissionForTender?.submittedAt || vendorBidSubmission?.submittedAt).format('MMM DD, YYYY HH:mm') : 'N/A'}</Text>
                </Group>
                <Group justify="flex-end" mt="md">
                  <Button
                    leftSection={<IconFileText size={16} />}
                    color="green"
                    onClick={() => {
                      if (existingSubmissionForTender?.tender?.id) {
                        navigate(`/vendor/submissions/${existingSubmissionForTender.tender.id}/${existingSubmissionForTender.id}`);
                      } else {
                        navigate(`/vendor/submissions`);
                      }
                    }}
                  >
                    View Submission
                  </Button>
                </Group>
              </Stack>
            </Card>
          )}

          {/* Draft Bid Submission */}
          {draftSubmission && !hasSubmittedBid && (
            <Card shadow="sm" p="md" withBorder>
              <Title order={4} mb="md">
                Draft Bid Submission
              </Title>
              <Stack gap="sm">
                <Alert color="blue" icon={<IconFileText size={16} />}>
                  <Text size="sm">
                    You have a saved draft bid for this tender. You can continue editing and submit when ready.
                  </Text>
                </Alert>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Total Price</Text>
                  <Text fw={500}>${Number(draftSubmission.totalPrice).toFixed(2)}</Text>
                </Group>
                <Group justify="flex-end" mt="md">
                  <Button
                    leftSection={<IconFileText size={16} />}
                    color="blue"
                    onClick={() => {
                      if (draftSubmission.tender?.id) {
                        navigate(`/vendor/submissions/${draftSubmission.tender.id}/${draftSubmission.id}`);
                      } else {
                        navigate(`/vendor/tenders/${tender.id}/submit-bid`);
                      }
                    }}
                  >
                    Continue Editing Draft
                  </Button>
                </Group>
              </Stack>
            </Card>
          )}

          <Group justify="flex-end" mt="md">
            {tender?.requireRegistration && !isRegistered && !deadlinePassed && (
              <Button
                leftSection={<IconUserPlus size={16} />}
                color="green"
                onClick={handleRegister}
                loading={registerForTender.isPending || loadingRegistrationStatus}
              >
                Register for Tender
              </Button>
            )}
            {tender?.requireRegistration && isRegistered && !deadlinePassed && (
              <Button
                color="red"
                variant="outline"
                leftSection={<IconUserMinus size={16} />}
                onClick={handleWithdraw}
                loading={withdrawFromTender.isPending || loadingRegistrationStatus}
              >
                Withdraw Registration
              </Button>
            )}
            {hasSubmittedBid && existingSubmissionForTender && (
              <Alert color="green" title="Bid Already Submitted" style={{ flex: 1 }}>
                <Text size="sm" mb="xs">
                  You have already submitted a bid for this tender. Only one submission per tender is allowed.
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => navigate(`/vendor/submissions/${existingSubmissionForTender.tender?.id}/${existingSubmissionForTender.id}`)}
                >
                  View Submission
                </Button>
              </Alert>
            )}
            {canSubmitBid && !hasSubmittedBid && !draftSubmission && (
              <Button
                leftSection={<IconFileText size={16} />}
                color="green"
                onClick={handleCreateDraftAndNavigate}
                loading={createDraftMutation.isPending}
                disabled={tender?.requireRegistration && !isRegistered}
              >
                Submit Bid
              </Button>
            )}
            {canSubmitBid && draftSubmission && !hasSubmittedBid && (
              <Button
                leftSection={<IconFileText size={16} />}
                color="blue"
                variant="light"
                onClick={() => {
                  if (draftSubmission.tender?.id) {
                    navigate(`/vendor/submissions/${draftSubmission.tender.id}/${draftSubmission.id}`);
                  } else {
                    navigate(`/vendor/tenders/${tender.id}/submit-bid`);
                  }
                }}
                disabled={tender?.requireRegistration && !isRegistered}
              >
                Continue Draft Bid
              </Button>
            )}
            {deadlinePassed && (
              <Alert color="yellow" title="Deadline Passed">
                The submission deadline for this tender has passed.
              </Alert>
            )}
          </Group>
        </Stack>
      </Paper>

    </Container>
  );
}

